import React, { useEffect, useState } from 'react'
import { List, useTable, ShowButton, EditButton, DateField } from '@refinedev/antd'
import { useList, useInvalidate } from '@refinedev/core'
import { App, Button, Form, Input, Modal, Select, Table, Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { useRowShow, ListPage, Pill, TableSurface, type Stat, type Tone } from '../../theme/listkit'
import { BadgeCheck, Clock, Ban, UsersRound } from '../../components/icons'
import { categoryLabel, GROUP_CATEGORY_OPTIONS } from './categoryOptions'
import { supabaseClient } from '../../lib/supabaseClient'
import { groupsAdminErrorMessage } from './adminErrors'

export type Group = Tables<'groups'>

/** Interest groups only — chapters have their own page (Classrooms). */
const INTEREST_FILTER = [{ field: 'kind', operator: 'eq', value: 'interest' }] as const

export const groupStatusTone = (status: string): Tone => {
  if (status === 'active') return 'green'
  if (status === 'pending_review') return 'gold'
  if (status === 'suspended') return 'red'
  return 'neutral' // archived, declined
}

/** Same text as the mobile PrivacyBadge (docs/groups/COMPONENTS-COPY.md §0). */
export const privacyLabel = (privacy: string): string => {
  if (privacy === 'open') return 'Open'
  if (privacy === 'request') return 'Request to join'
  if (privacy === 'invite') return 'Invite only'
  return privacy
}

/**
 * Groups — the interest-group roster. Class chapters are auto-created and
 * managed on their own page (Classrooms); this list is permanently
 * filtered to `kind = 'interest'` so the two surfaces never overlap.
 */
interface CreateGroupValues {
  name: string
  tagline?: string
  category: string
  privacy: string
}

export function GroupsList() {
  const { message } = App.useApp()
  const invalidate = useInvalidate()
  const { tableProps } = useTable<Group>({
    resource: 'groups',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    filters: { permanent: [...INTEREST_FILTER] },
  })
  const onRow = useRowShow('groups')

  // "Create group" (spec §2e) — a small fallback form that calls create_group
  // directly, so an admin can start an interest group from THIS dashboard
  // whatever mobile bundle they're running (an admin's create is always
  // 'active', never pending_review — create_group's own v_admin branch).
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createForm] = Form.useForm<CreateGroupValues>()

  async function submitCreate() {
    const values = await createForm.validateFields()
    setCreating(true)
    try {
      const { error } = await supabaseClient.rpc('create_group', {
        p_payload: {
          name: values.name,
          tagline: values.tagline || undefined,
          category: values.category,
          privacy: values.privacy,
        },
      })
      if (error) throw error
      message.success(`${values.name} is live`)
      setCreateOpen(false)
      createForm.resetFields()
      invalidate({ resource: 'groups', invalidates: ['list'] })
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setCreating(false)
    }
  }

  // Launch checklist (Groups Phase 4 Task 4): group_launch_status is a
  // security_invoker VIEW, not a Refine resource, so this batch-fetches
  // done_count/is_complete for the CURRENT page's ids and merges them in —
  // same "direct supabaseClient read alongside the table" shape as the
  // Proposals/Ideas queues, just batched instead of per-row.
  const [launchById, setLaunchById] = useState<Record<string, { done_count: number; is_complete: boolean }>>({})
  const pageIds = (tableProps.dataSource ?? []).map((g) => g.id).join(',')
  useEffect(() => {
    const ids = pageIds ? pageIds.split(',') : []
    if (ids.length === 0) return
    let cancelled = false
    supabaseClient
      .from('group_launch_status')
      .select('group_id, done_count, is_complete')
      .in('group_id', ids)
      .then(({ data }) => {
        if (cancelled) return
        const next: Record<string, { done_count: number; is_complete: boolean }> = {}
        for (const row of data ?? []) {
          if (row.group_id) next[row.group_id] = { done_count: row.done_count ?? 0, is_complete: Boolean(row.is_complete) }
        }
        setLaunchById(next)
      })
    return () => {
      cancelled = true
    }
  }, [pageIds])

  // Three bounded pageSize:1 count queries, each scoped to interest groups too
  // (same shape as the Class Year Changes queue) so the deck matches the table.
  const activeQ = useList<Group>({
    resource: 'groups',
    filters: [...INTEREST_FILTER, { field: 'status', operator: 'eq', value: 'active' }],
    pagination: { pageSize: 1 },
  })
  const pendingQ = useList<Group>({
    resource: 'groups',
    filters: [...INTEREST_FILTER, { field: 'status', operator: 'eq', value: 'pending_review' }],
    pagination: { pageSize: 1 },
  })
  const suspendedQ = useList<Group>({
    resource: 'groups',
    filters: [...INTEREST_FILTER, { field: 'status', operator: 'eq', value: 'suspended' }],
    pagination: { pageSize: 1 },
  })

  const active = activeQ.result?.total ?? 0
  const pending = pendingQ.result?.total ?? 0
  const suspended = suspendedQ.result?.total ?? 0

  const stats: Stat[] = [
    { label: 'Active', value: active.toLocaleString(), icon: BadgeCheck, tone: active > 0 ? 'green' : 'neutral' },
    { label: 'Pending review', value: pending.toLocaleString(), icon: Clock, tone: pending > 0 ? 'gold' : 'neutral' },
    { label: 'Suspended', value: suspended.toLocaleString(), icon: Ban, tone: suspended > 0 ? 'red' : 'neutral' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Groups',
        description:
          'Interest groups members create or ask to join — faith, career, fitness, arts and more. Class chapters (one per class year) live on their own page. Open a group to review its roster, feature it, or change its status.',
        actions: (
          <Button type="primary" onClick={() => setCreateOpen(true)} data-testid="groups-create">
            Create group
          </Button>
        ),
      }}
      stats={stats}
      statColumns={3}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Group>
              dataIndex="name"
              title="Name"
              render={(value: string, record) => (
                <span>
                  <span style={{ fontFamily: brand.fSans, fontWeight: 700, color: brand.ink }}>{value}</span>
                  {record.tagline && (
                    <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11.5, color: brand.sub }}>
                      {record.tagline}
                    </div>
                  )}
                </span>
              )}
            />
            <Table.Column<Group>
              dataIndex="category"
              title="Category"
              render={(value: string) => <Pill tone="jaguar">{categoryLabel(value)}</Pill>}
            />
            <Table.Column<Group>
              dataIndex="privacy"
              title="Privacy"
              render={(value: string) => <Pill tone="neutral">{privacyLabel(value)}</Pill>}
            />
            <Table.Column<Group>
              dataIndex="status"
              title="Status"
              render={(value: string) => <Pill tone={groupStatusTone(value)}>{value}</Pill>}
            />
            <Table.Column<Group>
              dataIndex="member_count"
              title="Members"
              sorter
              render={(value: number) => (
                <Space size={4}>
                  <UsersRound size={13} color={brand.sub} />
                  <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>
                    {(value ?? 0).toLocaleString()}
                  </span>
                </Space>
              )}
            />
            <Table.Column<Group>
              dataIndex="launch"
              title="Launch"
              render={(_: unknown, record) => {
                const launch = launchById[record.id]
                if (!launch) return <span style={{ color: brand.sub }}>—</span>
                return <Pill tone={launch.is_complete ? 'green' : 'neutral'}>{`${launch.done_count}/5`}</Pill>
              }}
            />
            <Table.Column<Group>
              dataIndex="featured_rank"
              title="Featured"
              render={(value: number | null) => (value == null ? '—' : <Pill tone="gold">#{value}</Pill>)}
            />
            <Table.Column<Group>
              dataIndex="created_at"
              title="Created"
              sorter
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Group>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>

      <Modal
        title="Create group"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false)
          createForm.resetFields()
        }}
        onOk={submitCreate}
        okText="Create"
        confirmLoading={creating}
      >
        <Form form={createForm} layout="vertical" initialValues={{ category: 'other', privacy: 'open' }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input data-testid="groups-create-name" />
          </Form.Item>
          <Form.Item name="tagline" label="Tagline">
            <Input data-testid="groups-create-tagline" />
          </Form.Item>
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select options={GROUP_CATEGORY_OPTIONS} data-testid="groups-create-category" />
          </Form.Item>
          <Form.Item name="privacy" label="Privacy" rules={[{ required: true }]}>
            <Select
              data-testid="groups-create-privacy"
              options={[
                { value: 'open', label: 'Open' },
                { value: 'request', label: 'Request to join' },
                { value: 'invite', label: 'Invite only' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </ListPage>
  )
}
